import type { WalletSendTransactionFunc } from '@/types/wallet';
import { fogoSession } from './useFogoSession';
import { tryCatch } from '@/types/utils';
import { markRaw } from 'vue';
import { solanaConnection } from './useSolanaConnection';
import type { VersionedTransaction } from '@solana/web3.js';
import type { CommonResponse } from '@/types/response';
import { sendTransactionService } from '@/services/solanaTransactionService';
import { useWallet as useSolanaWallet } from '@alphabatem/vue2-wallet-adapter';

export function useWalletOperations() {
  const wallet = markRaw(useSolanaWallet());

  async function sendTransaction({
    transaction,
  }: {
    transaction: VersionedTransaction;
  }): Promise<CommonResponse<string>> {
    const sendTransactionImpl = getSendTransaction();

    return sendTransactionService.sendTransaction({
      transaction,
      sendTransaction: sendTransactionImpl,
    });
  }

  function getSendTransaction(): WalletSendTransactionFunc {
    // fogo send transaction
    if (fogoSession.session.value) {
      return ({ transaction }) => {
        return tryCatch<string>(async () => {
          const { error, data: signature } = await fogoSession.sendTransaction({
            transaction: transaction as VersionedTransaction,
          });

          if (error) {
            return { error, ok: false };
          }

          if (!signature) {
            return { error: new Error('No signature returned'), ok: false };
          }

          return { data: signature, ok: true };
        });
      };
    }

    // wallet send transaction
    return ({ transaction }) => {
      return tryCatch<string>(async () => {
        if (!wallet.signTransaction.value) {
          return { error: new Error('Wallet not connected'), ok: false };
        }

        const signedTx = await wallet.signTransaction.value(transaction);

        const signature = await solanaConnection.connection.sendRawTransaction(
          signedTx.serialize(),
          {
            skipPreflight: true,
          },
        );

        return { data: signature, ok: true };
      });
    };
  }

  return {
    getSendTransaction,
    sendTransaction,
  };
}
